import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useState } from "react";
import { Badge, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import type { Account } from "../../../../api/src/accounts/accounts.service";
import {
  resolveAccountTaxType,
  type TaxRegistration,
} from "../../lib/tax-registration";
import FormFieldError from "../../components/FormFieldError";
import SubmitButton from "../../components/SubmitButton";
import { useAuthenticatedMutationNew } from "../../lib/api";
import { NotificationContext } from "../../Notifications";

const TAX_REG_OPTIONS = [
  { value: "", label: "— Use Bridge Default —" },
  { value: "taxable", label: "Taxable (Brokerage)" },
  { value: "tax_deferred", label: "Tax-Deferred (Trad IRA, 401k, SEP)" },
  { value: "tax_free", label: "Tax-Free (Roth IRA, Roth 401k, HSA)" },
  { value: "inherited_deferred", label: "Inherited IRA (Traditional)" },
  { value: "inherited_free", label: "Inherited Roth IRA" },
] as const;

const schema: yup.ObjectSchema<{ taxRegistration: string }> = yup.object({
  taxRegistration: yup.string().defined().label("Tax Registration"),
});

function useUpdateTaxRegistration(accountId: number) {
  return useAuthenticatedMutationNew<
    void,
    void,
    { taxRegistration: string | null }
  >({
    // `PUT /accounts/:id/tax-registration` is served at `/v1` only
    // (APP-1653). The key keeps `"accounts"` first, so `isAccountWrite`
    // (`./lib.ts`) matches it; the url is under `/v1`.
    mutationKey: ["PUT", "accounts", accountId, "tax-registration"],
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: () => null,
  });
}

const AccountTaxRegistrationField = ({
  account,
  accountId,
}: {
  account?: Account;
  accountId?: number;
}) => {
  const {
    handleSubmit,
    setFocus,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<{ taxRegistration: string }>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    reset({ taxRegistration: (account as any)?.taxRegistration ?? "" });
  }, [(account as any)?.taxRegistration, reset]);

  const updateTaxRegistration = useUpdateTaxRegistration(accountId ?? -1);

  const queryClient = useQueryClient();
  const notificationContext = useContext(NotificationContext);

  const [enableField, setEnableField] = useState(false);

  const onSubmit = useCallback(
    async ({ taxRegistration }: { taxRegistration: string }) => {
      try {
        const value = taxRegistration || null;
        await updateTaxRegistration.mutateAsync({ taxRegistration: value });
        setEnableField(false);
        queryClient.setQueryData(
          ["accounts", accountId ?? -1, { includeBalances: true }],
          { ...account, taxRegistration: value },
        );
        reset({ taxRegistration });
        notificationContext.pushNotification({
          id: `account-${accountId}-tax-registration`,
          header: "Tax Classification Updated",
          body: `${account?.displayName} updated`,
          variant: "success",
        });
      } catch (err) {
        console.error("Failed to save tax registration", err);
        notificationContext.pushNotification({
          id: `account-${accountId}-tax-registration`,
          header: "Failed to Update Tax Classification",
          body: `${account?.displayName} was not saved`,
          variant: "danger",
        });
      }
    },
    [
      account,
      accountId,
      notificationContext,
      queryClient,
      reset,
      updateTaxRegistration,
    ],
  );

  const onSave = useCallback(
    (ev: React.MouseEvent) => {
      if (!enableField) {
        ev.preventDefault();
        setEnableField(true);
      }
    },
    [enableField],
  );

  useEffect(() => {
    if (enableField) {
      setFocus("taxRegistration");
    }
  }, [enableField, setFocus]);

  const taxRegistration = watch("taxRegistration");
  const { label, isOverridden, isInherited } = resolveAccountTaxType({
    taxRegistration: (taxRegistration || null) as TaxRegistration | null,
    isTaxable: account?.isTaxable,
    isTaxDeferred: account?.isTaxDeferred,
    type: account?.type,
    displayName: account?.displayName,
    owner: account?.owner,
  });

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div className="d-flex align-items-center">
        <div style={{ maxWidth: 360 }}>
          {enableField ? null : (
            <span>
              {label}
              {isOverridden ? (
                <Badge
                  bg="secondary"
                  className="ms-2"
                  style={{ fontSize: "0.7em" }}
                >
                  Manual
                </Badge>
              ) : (
                <Badge
                  bg="info"
                  text="dark"
                  className="ms-2"
                  style={{ fontSize: "0.7em" }}
                >
                  Bridge
                </Badge>
              )}
              {isInherited ? (
                <Badge
                  bg="warning"
                  text="dark"
                  className="ms-2"
                  style={{ fontSize: "0.7em" }}
                >
                  Inherited
                </Badge>
              ) : null}
            </span>
          )}
          <Form.Group controlId="form-taxRegistration" hidden={!enableField}>
            <Form.Select
              value={taxRegistration}
              onChange={(ev) =>
                setValue("taxRegistration", ev.currentTarget.value)
              }
            >
              {TAX_REG_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
            <FormFieldError field={errors.taxRegistration} />
          </Form.Group>
        </div>
        <SubmitButton
          variant="icon"
          icon={enableField ? "/icons/save.svg" : "/icons/edit.svg"}
          label={enableField ? "Save" : "Edit"}
          type={enableField ? "submit" : "button"}
          onClick={onSave}
          isSubmitting={isSubmitting}
          className="ms-2 me-3"
        />
      </div>
    </Form>
  );
};

export default AccountTaxRegistrationField;
